import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Button, Input, Spin, Empty, Card, Tag, Tooltip, Drawer } from "antd";
import moment from "moment";
import Swal from "sweetalert2";
import {
  Filter,
  Maximize2,
  Minimize2,
  Edit,
  Trash2,
  Grid,
  RefreshCw,
  PlusCircle,
  Layers,
} from "react-feather";
import Select from "react-select";
import axios from "axios";
import { useNotification } from "../../context/NotificationContext";

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
const token = localStorage.getItem("token");

const getPortalTarget = () => (typeof document !== "undefined" ? document.body : null);

const drawerSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
  menu: (base) => ({
    ...base,
    zIndex: 2147483647,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
  }),
};

const activeOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const ItemCountCell = ({ category_id }) => {
  const [itemCount, setItemCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchItemCount = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiURL}/pos/items/listings?category_id=${category_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const count = Array.isArray(response.data)
          ? response.data.length
          : response.data.menus?.length || response.data.items?.length || 0;

        if (isMounted) setItemCount(count);
      } catch (err) {
        console.error("Error fetching item count:", err);
        if (isMounted) setItemCount(0);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchItemCount();

    return () => {
      isMounted = false;
    };
  }, [category_id]);

  return (
    <span className="ns-cat-items-chip">
      {loading ? <Spin size="small" /> : itemCount}
    </span>
  );
};

const CategoryList = () => {
  const [searchText, setSearchText] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", is_active: true });

  const { Search } = Input;
  const { notify } = useNotification();

  const fetchCategories = useCallback(async () => {
    setTableLoading(true);

    try {
      const response = await axios.get(`${apiURL}/pos/categories/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fetchedCategories = Array.isArray(response.data)
        ? response.data
        : response.data.categories || [];

      setCategories(fetchedCategories);
      setError(fetchedCategories.length === 0 ? "No categories found" : null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to fetch categories";
      setError(errorMessage);
      notify("error", errorMessage);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      const categoryName = String(category.name || "").toLowerCase();
      const matchesSearch = categoryName.includes(searchText.toLowerCase());
      const matchesActive =
        activeFilter === null || category.is_active === (activeFilter.value === "active");

      return matchesSearch && matchesActive;
    });
  }, [categories, searchText, activeFilter]);

  const totalCategories = categories.length;
  const activeCount = categories.filter((category) => category.is_active).length;
  const inactiveCount = totalCategories - activeCount;

  const getDynamicHeight = () => Math.max(380, window.innerHeight - 330);

  const handleFilterReset = () => {
    setActiveFilter(null);
    setSearchText("");
  };

  const openCategoryDrawer = (mode, category = null) => {
    setModalMode(mode);

    if (mode === "edit" && category) {
      setEditingCategory(category);
      setFormData({ name: category.name || "", is_active: !!category.is_active });
    } else {
      setEditingCategory(null);
      setFormData({ name: "", is_active: true });
    }

    setIsCategoryDrawerOpen(true);
  };

  const closeCategoryDrawer = () => {
    setIsCategoryDrawerOpen(false);
    setEditingCategory(null);
    setFormData({ name: "", is_active: true });
  };

  const handleModalSubmit = async (event) => {
    event.preventDefault();

    const name = String(formData.name || "").trim();

    if (!name) {
      notify("error", "Category name is required");
      return;
    }

    try {
      let updatedCategory;

      if (modalMode === "add") {
        const response = await axios.post(
          `${apiURL}/pos/categories/add`,
          { name, is_active: !!formData.is_active },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        updatedCategory = response.data;
        setCategories((previous) => [...previous, updatedCategory]);
        notify("success", "Category added successfully!");
      } else {
        const response = await axios.put(
          `${apiURL}/pos/categories/edit/${editingCategory.category_id}`,
          { name, is_active: !!formData.is_active },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        updatedCategory = response.data;
        setCategories((previous) =>
          previous.map((category) =>
            category.category_id === editingCategory.category_id ? updatedCategory : category
          )
        );
        notify("success", "Category updated successfully!");
      }

      closeCategoryDrawer();
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to save category";
      notify("error", errorMessage);
    }
  };

  const handleDelete = (category_id) => {
    Swal.fire({
      title: "Delete category?",
      text: "This will delete the category and may affect associated menu items.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "ns-swal-popup",
        confirmButton: "ns-swal-confirm",
        cancelButton: "ns-swal-cancel",
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await axios.delete(`${apiURL}/pos/categories/delete/${category_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCategories((previous) => previous.filter((category) => category.category_id !== category_id));
        notify("success", "Category deleted successfully!");
      } catch (err) {
        const errorMessage = err.response?.data?.error || "Failed to delete category";
        notify("error", errorMessage);
      }
    });
  };

  const handleToggleStatus = async (category_id, currentStatus) => {
    try {
      const response = await axios.put(
        `${apiURL}/pos/categories/edit/${category_id}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedCategory = response.data;

      setCategories((previous) =>
        previous.map((category) =>
          category.category_id === category_id ? updatedCategory : category
        )
      );
      notify("success", "Category status updated successfully!");
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to update category status";
      notify("error", errorMessage);
    }
  };

  const columns = [
    {
      title: "Category",
      dataIndex: "name",
      render: (name, record) => (
        <div className="ns-cat-name-cell">
          <span className="ns-cat-avatar">{String(name || "C").charAt(0).toUpperCase()}</span>
          <div style={{ minWidth: 0 }}>
            <strong title={name}>{name}</strong>
            <span>Created {record.created_at ? moment(record.created_at).format("DD MMM YYYY") : "N/A"}</span>
          </div>
        </div>
      ),
      sorter: (a, b) => String(a.name || "").localeCompare(String(b.name || "")),
      width: 300,
      fixed: "left",
    },
    {
      title: "Items",
      dataIndex: "category_id",
      render: (category_id) => <ItemCountCell category_id={category_id} />,
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (is_active, record) => (
        <div className="ns-cat-status-wrap">
          <label className="ns-cat-toggle" htmlFor={`toggle-${record.category_id}`}>
            <input
              type="checkbox"
              id={`toggle-${record.category_id}`}
              checked={!!is_active}
              onChange={() => handleToggleStatus(record.category_id, is_active)}
            />
            <span />
          </label>
          <Tag color={is_active ? "success" : "error"} className="ns-cat-status-tag">
            {is_active ? "Active" : "Inactive"}
          </Tag>
        </div>
      ),
      width: 170,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date) => (date ? moment(date).format("DD MMM YYYY HH:mm") : "N/A"),
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      width: 190,
    },
    {
      title: "Actions",
      dataIndex: "category_id",
      render: (category_id, record) => (
        <div className="ns-cat-actions">
          <Tooltip title="Edit category">
            <Button type="text" icon={<Edit size={17} />} onClick={() => openCategoryDrawer("edit", record)} />
          </Tooltip>
          <Tooltip title="Delete category">
            <Button type="text" danger icon={<Trash2 size={17} />} onClick={() => handleDelete(category_id)} />
          </Tooltip>
        </div>
      ),
      width: 120,
      fixed: "right",
    },
  ];

  return (
    <div className={`page-wrapper ${isExpanded ? "ns-cat-expanded" : ""}`}>
      <style>{`
        .ns-cat-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.07), transparent 30%),
            radial-gradient(circle at 92% 2%, rgba(34, 197, 94, 0.07), transparent 28%),
            #f8fafc;
          color: #0f172a;
          padding-bottom: 22px;
        }

        .ns-cat-expanded {
          position: fixed !important;
          inset: 0 !important;
          z-index: 2147482500;
          background: #f8fafc;
          overflow: auto;
        }

        .ns-cat-hero {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.15), transparent 34%),
            radial-gradient(circle at top right, rgba(34, 197, 94, 0.10), transparent 32%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07);
          padding: 16px 18px;
          margin-bottom: 14px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          flex-wrap: wrap;
        }

        .ns-cat-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-cat-main-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 16px 32px rgba(37, 99, 235, 0.22);
          flex: 0 0 auto;
        }

        .ns-cat-eyebrow {
          color: #2563eb;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-cat-title {
          color: #001f5b;
          font-size: 1.34rem;
          line-height: 1.18;
          font-weight: 950;
          margin: 0;
        }

        .ns-cat-subtitle {
          color: #64748b;
          font-size: 0.84rem;
          line-height: 1.5;
          margin: 6px 0 0;
          max-width: 760px;
        }

        .ns-cat-stats {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .ns-cat-stat {
          min-height: 46px;
          min-width: 104px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.045);
          padding: 8px 11px;
        }

        .ns-cat-stat span {
          display: block;
          color: #64748b;
          font-size: 0.68rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .ns-cat-stat strong {
          display: block;
          color: #0f172a;
          font-size: 1.05rem;
          line-height: 1.15;
          font-weight: 950;
        }

        .ns-cat-toolbar {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
          padding: 12px;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .ns-cat-toolbar-left {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1 1 420px;
          min-width: 280px;
        }

        .ns-cat-toolbar-left .ant-input-search {
          max-width: 460px;
        }

        .ns-cat-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ns-cat-btn {
          min-height: 40px;
          border-radius: 14px !important;
          font-weight: 900 !important;
          display: inline-flex !important;
          align-items: center;
          gap: 7px;
        }

        .ns-cat-btn-primary {
          background: linear-gradient(135deg, #16a34a, #22c55e) !important;
          border-color: #16a34a !important;
          color: #ffffff !important;
          box-shadow: 0 14px 30px rgba(22, 163, 74, 0.18);
        }

        .ns-cat-table-card {
          border-radius: 22px !important;
          border: 1px solid rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07) !important;
          overflow: hidden;
        }

        .ns-cat-table-card .ant-card-body {
          padding: 0 !important;
        }

        .ns-cat-name-cell {
          display: flex;
          align-items: center;
          gap: 11px;
          min-width: 0;
        }

        .ns-cat-avatar {
          width: 38px;
          height: 38px;
          border-radius: 14px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 950;
          flex: 0 0 auto;
        }

        .ns-cat-name-cell strong {
          display: block;
          color: #0f172a;
          font-weight: 950;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .ns-cat-name-cell span {
          display: block;
          color: #64748b;
          font-size: 0.72rem;
          margin-top: 2px;
        }

        .ns-cat-items-chip {
          min-width: 44px;
          min-height: 30px;
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 950;
          padding: 0 10px;
        }

        .ns-cat-status-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ns-cat-status-tag {
          border-radius: 999px !important;
          font-weight: 900 !important;
          margin-inline-end: 0 !important;
        }

        .ns-cat-toggle {
          position: relative;
          width: 46px;
          height: 26px;
          margin: 0;
          cursor: pointer;
        }

        .ns-cat-toggle input {
          display: none;
        }

        .ns-cat-toggle span {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          background: #cbd5e1;
          transition: background 0.16s ease;
        }

        .ns-cat-toggle span::after {
          content: "";
          position: absolute;
          top: 4px;
          left: 4px;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #ffffff;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.18);
          transition: transform 0.16s ease;
        }

        .ns-cat-toggle input:checked + span {
          background: #2563eb;
        }

        .ns-cat-toggle input:checked + span::after {
          transform: translateX(20px);
        }

        .ns-cat-actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .ns-cat-empty {
          min-height: 420px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px;
        }

        .ns-cat-drawer .ant-drawer-content-wrapper {
          width: min(480px, 100vw) !important;
        }

        .ns-cat-drawer .ant-drawer-content {
          background: #f8fafc;
        }

        .ns-cat-drawer-root .ant-drawer-mask {
          background: rgba(15, 23, 42, 0.045) !important;
          backdrop-filter: blur(0.8px);
          -webkit-backdrop-filter: blur(0.8px);
        }

        .ns-cat-drawer-root .ant-drawer-content-wrapper {
          box-shadow: -18px 0 48px rgba(15, 23, 42, 0.14) !important;
        }

        .ns-cat-drawer-root .ant-drawer-body {
          padding: 0 !important;
          background: #f8fafc;
        }

        .ns-cat-drawer-root .ant-drawer-footer {
          background: rgba(255, 255, 255, 0.98) !important;
          border-top: 1px solid rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
          padding: 12px 16px !important;
        }

        .ns-cat-drawer-head {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 34%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 16px 18px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 14px;
        }

        .ns-cat-drawer-head-left {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-cat-drawer-head-icon {
          width: 44px;
          height: 44px;
          border-radius: 16px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
          flex: 0 0 auto;
        }

        .ns-cat-drawer-eyebrow {
          color: #2563eb;
          font-size: 0.68rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-cat-drawer-title {
          color: #001f5b;
          font-size: 1.18rem;
          line-height: 1.15;
          font-weight: 950;
          margin: 0;
        }

        .ns-cat-drawer-subtitle {
          color: #64748b;
          font-size: 0.77rem;
          line-height: 1.42;
          font-weight: 760;
          margin: 5px 0 0;
        }

        .ns-cat-drawer-close {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(220, 38, 38, 0.16);
          background: #fff1f2;
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.35rem;
          line-height: 1;
          flex: 0 0 auto;
          transition: transform 0.16s ease, background 0.16s ease;
        }

        .ns-cat-drawer-close:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .ns-cat-drawer-form {
          padding: 16px;
        }

        .ns-cat-form-section-title {
          color: #0f172a;
          font-size: 0.82rem;
          font-weight: 950;
          margin: 0 0 12px;
        }

        .ns-cat-drawer-footer-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .ns-cat-drawer-footer-actions .ant-btn {
          min-height: 40px;
          border-radius: 13px !important;
          font-weight: 900 !important;
          padding-left: 16px !important;
          padding-right: 16px !important;
        }

        .ns-cat-drawer-footer-actions .ant-btn-primary {
          background: linear-gradient(135deg, #16a34a, #22c55e) !important;
          border-color: #16a34a !important;
          box-shadow: 0 14px 30px rgba(22, 163, 74, 0.18);
        }


        .ns-cat-drawer .ant-drawer-header {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 34%),
            #ffffff;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 18px;
        }

        .ns-cat-drawer .ant-drawer-title {
          color: #001f5b;
          font-weight: 950;
        }

        .ns-cat-drawer-body {
          display: grid;
          gap: 13px;
        }

        .ns-cat-drawer-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
          padding: 15px;
          display: grid;
          gap: 13px;
        }

        .ns-cat-field {
          display: grid;
          gap: 7px;
        }

        .ns-cat-field label {
          color: #0f172a;
          font-size: 0.77rem;
          font-weight: 950;
          margin: 0;
        }

        .ns-cat-input {
          width: 100%;
          min-height: 44px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #ffffff;
          color: #0f172a;
          font-size: 0.88rem;
          font-weight: 760;
          outline: none;
          padding: 0 13px;
        }

        .ns-cat-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .ns-cat-switch-card {
          min-height: 58px;
          border-radius: 16px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: #f8fafc;
          padding: 10px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .ns-cat-switch-card strong {
          display: block;
          color: #0f172a;
          font-size: 0.78rem;
          font-weight: 950;
        }

        .ns-cat-switch-card span {
          display: block;
          color: #64748b;
          font-size: 0.7rem;
          font-weight: 720;
          margin-top: 2px;
        }

        .ns-cat-drawer-footer {
          display: none;
        }

        .ns-cat-filter-summary {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 760;
        }

        @media (max-width: 768px) {
          .ns-cat-hero,
          .ns-cat-toolbar {
            align-items: stretch;
          }

          .ns-cat-stats {
            justify-content: flex-start;
          }

          .ns-cat-toolbar-left,
          .ns-cat-toolbar-left .ant-input-search {
            width: 100%;
            max-width: 100%;
          }
        }
      `}</style>

      <div className="content container-fluid ns-cat-page">
        <section className="ns-cat-hero">
          <div className="ns-cat-title-wrap">
            <span className="ns-cat-main-icon">
              <Layers size={22} />
            </span>
            <div>
              <div className="ns-cat-eyebrow">POS Menu Setup</div>
              <h3 className="ns-cat-title">Menu Categories</h3>
              <p className="ns-cat-subtitle">
                Organize menu items into cashier-friendly groups for faster KOT creation, cleaner billing, and better digital menu navigation.
              </p>
            </div>
          </div>

          <div className="ns-cat-stats">
            <div className="ns-cat-stat">
              <span>Total</span>
              <strong>{totalCategories}</strong>
            </div>
            <div className="ns-cat-stat">
              <span>Active</span>
              <strong>{activeCount}</strong>
            </div>
            <div className="ns-cat-stat">
              <span>Inactive</span>
              <strong>{inactiveCount}</strong>
            </div>
          </div>
        </section>

        <section className="ns-cat-toolbar">
          <div className="ns-cat-toolbar-left">
            <Search
              placeholder="Search categories..."
              allowClear
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              size="large"
            />
            <span className="ns-cat-filter-summary">
              Showing {filteredCategories.length} of {totalCategories}
            </span>
          </div>

          <div className="ns-cat-toolbar-actions">
            <Button className="ns-cat-btn" onClick={() => setIsFilterDrawerOpen(true)}>
              <Filter size={16} />
              Filters
            </Button>

            <Button className="ns-cat-btn" onClick={fetchCategories}>
              <RefreshCw size={16} />
              Refresh
            </Button>

            <Button className="ns-cat-btn" onClick={() => setIsExpanded((previous) => !previous)}>
              {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {isExpanded ? "Minimize" : "Maximize"}
            </Button>

            <Button className="ns-cat-btn ns-cat-btn-primary" type="primary" onClick={() => openCategoryDrawer("add")}>
              <PlusCircle size={16} />
              Add Category
            </Button>
          </div>
        </section>

        <Card className="ns-cat-table-card">
          {loading ? (
            <div className="ns-cat-empty">
              <Spin size="large" />
            </div>
          ) : error ? (
            <div className="ns-cat-empty">
              <Empty description={error} />
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="ns-cat-empty">
              <Empty description="No categories match your current filters." />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredCategories}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
              loading={tableLoading}
              className="table-striped custom-table"
              rowKey={(record) => record.category_id}
              scroll={{ x: 900, y: getDynamicHeight() }}
              sticky
            />
          )}
        </Card>

        <Drawer
          title="Filter Categories"
          placement="right"
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          className="ns-cat-drawer"
          rootClassName="ns-cat-drawer-root"
          width={420}
          destroyOnClose={false}
          bodyStyle={{ padding: 16 }}
          maskStyle={{
            background: "rgba(15, 23, 42, 0.045)",
            backdropFilter: "blur(0.8px)",
          }}
          footerStyle={{ padding: "12px 16px" }}
          footer={
            <div className="ns-cat-drawer-footer-actions">
              <Button onClick={handleFilterReset}>Reset</Button>
              <Button type="primary" onClick={() => setIsFilterDrawerOpen(false)}>
                Apply
              </Button>
            </div>
          }
        >
          <div className="ns-cat-drawer-body">
            <div className="ns-cat-drawer-card">
              <div className="ns-cat-field">
                <label>Status</label>
                <Select
                  options={activeOptions}
                  placeholder="Select status"
                  value={activeFilter}
                  onChange={setActiveFilter}
                  isClearable
                  styles={drawerSelectStyles}
                  menuPortalTarget={getPortalTarget()}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuShouldScrollIntoView={false}
                />
              </div>
            </div>
          </div>
        </Drawer>

        <Drawer
          title={null}
          closable={false}
          placement="right"
          open={isCategoryDrawerOpen}
          onClose={closeCategoryDrawer}
          className="ns-cat-drawer"
          rootClassName="ns-cat-drawer-root"
          width={520}
          destroyOnClose
          bodyStyle={{ padding: 0 }}
          maskStyle={{
            background: "rgba(15, 23, 42, 0.045)",
            backdropFilter: "blur(0.8px)",
          }}
          footerStyle={{ padding: "12px 16px" }}
          footer={
            <div className="ns-cat-drawer-footer-actions">
              <Button onClick={closeCategoryDrawer}>Cancel</Button>
              <Button type="primary" htmlType="submit" form="ns-category-form">
                Save Category
              </Button>
            </div>
          }
        >
          <div className="ns-cat-drawer-head">
            <div className="ns-cat-drawer-head-left">
              <span className="ns-cat-drawer-head-icon">
                <Layers size={21} />
              </span>
              <div>
                <div className="ns-cat-drawer-eyebrow">Menu Category</div>
                <h5 className="ns-cat-drawer-title">
                  {modalMode === "add" ? "Add Category" : "Edit Category"}
                </h5>
                <p className="ns-cat-drawer-subtitle">
                  Create cashier-friendly groups for faster KOT selection and cleaner menu navigation.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="ns-cat-drawer-close"
              onClick={closeCategoryDrawer}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          </div>

          <form id="ns-category-form" onSubmit={handleModalSubmit} className="ns-cat-drawer-form">
            <div className="ns-cat-drawer-card">
              <h6 className="ns-cat-form-section-title">Basic Details</h6>

              <div className="ns-cat-field">
                <label>Name <span style={{ color: "#dc2626" }}>*</span></label>
                <input
                  type="text"
                  className="ns-cat-input"
                  placeholder="Enter category name"
                  value={formData.name}
                  onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                  required
                />
              </div>
            </div>

            <div className="ns-cat-drawer-card" style={{ marginTop: 13 }}>
              <h6 className="ns-cat-form-section-title">Availability</h6>

              <div className="ns-cat-switch-card">
                <div>
                  <strong>Active Status</strong>
                  <span>Control whether this category is available for use.</span>
                </div>
                <label className="ns-cat-toggle" htmlFor="modal-toggle">
                  <input
                    type="checkbox"
                    id="modal-toggle"
                    checked={!!formData.is_active}
                    onChange={(event) => setFormData({ ...formData, is_active: event.target.checked })}
                  />
                  <span />
                </label>
              </div>
            </div>
          </form>
        </Drawer>
      </div>
    </div>
  );
};

export default CategoryList;
